/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-03 14:39:41
 **/
/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { useMemo, useState } from "react";
import { css, useTheme } from "@emotion/react";
import { useAtomValue } from "jotai";

import { Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { Maybe } from "@/frameworks/libraries/components";
import { JotaiStore } from "@/frameworks/libraries/commons";
import { RestAPIError } from "@/frameworks/libraries/restservice";
import { Button, Menu, TextField, useToast } from "@/frameworks/designStuff/components";
import { Column, Divider, Icon, Row, Typography } from "@/frameworks/designStuff/components";

import { Avatar } from "@/frameworks/core/components";
import { PhoneCallAtoms, usePhoneCall } from "@/frameworks/core/hooks";
import { useChatMessageActions, useChatRoomActions } from "@/frameworks/core/chat";
import { APIErrorCode } from "@/frameworks/core/commons";

import * as S from "./emotion";
import { usePersistActions } from "@/frameworks/core/persistence";

const LOGTAG = "#IncomingCall#";

export default function IncomingCall({ callID, callerID, callerDeviceKey }: PushServices.Call.PhoneCallPopupItem) {
	const [customMessage, setCustomMessage] = useState("");
	const { colors } = useTheme();
	const toast = useToast();
	const phoneCall = usePhoneCall();
	const callUsers = phoneCall.state.users(callID)[0];
	const { users } = usePersistActions();
	const [isOpen, setIsOpen] = useState(false);
	const [isAccepted, setAccepted] = useState(false);
	const callStatus = phoneCall.status(callID);
	const call = useAtomValue(PhoneCallAtoms.Call(callID), { store: JotaiStore });
	const myMedia = useMemo(
		() => callUsers?.[Configure.userID].media ?? phoneCall.default_user_media_info,
		[callUsers]
	);

	const handleAccept = async () => {
		setAccepted(true);
		await phoneCall.call_accept(callID).then(() => setAccepted(false));
	};

	const handleReject = () => {
		phoneCall.call_reject(callID);
	};
	const { sendMessage } = useChatMessageActions();
	const chatRoomActions = useChatRoomActions();

	const getDMRoomID = async () => {
		try {
			const response = await chatRoomActions.dm.create({ userIDs: [callerID] });
			if (!response) return;
			return response.id;
		} catch (err) {
			const error = err as RestAPIError;
			switch (error.code) {
				case APIErrorCode.EXIST_SAME_CHAT_MEMBER: {
					const { roomId } = error.data.payload;
					return roomId;
				}
			}
		}
	};

	const handleRejectItem = (ev: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
		const rejectMessage = ev.currentTarget.firstElementChild?.textContent || "";
		sendRejectMessage(rejectMessage);
	};

	const sendRejectMessage = async (message: string) => {
		const roomID = await getDMRoomID();
		sendMessage({
			emergency: false,
			message: JSON.stringify({
				type: "richtext",
				elements: [
					{
						type: "text",
						text: message
					}
				]
			}),
			roomId: roomID
		}).then(() => {
			toast.show({ type: "system", message: _T("전송되었습니다.") });
			handleReject();
			setIsOpen(false);
		});
	};

	return (
		<S.ReceivePopupWrapper id={callID} horizonAlign="center" gap={12}>
			<S.CallInfoWraper gap={12} horizonAlign="center">
				<Avatar id={callerID} kind="people_64" />
				<Column gap={1} horizonAlign="center" style={{ wordBreak: "break-all", textAlign: "center" }}>
					<Typography typoColor="neutral_00_100" typoSize="Title1_B">
						{_T("{{name}} 님으로부터 전화", { name: call?.callerName ?? call?.callerCallNumber })}
					</Typography>
					<Typography typoColor="neutral_00_68" typoSize="Body3_MD">
						{users.get_user(call.callerId)?.username}
					</Typography>
				</Column>
			</S.CallInfoWraper>
			<Row style={{ width: "100%", paddingBottom: "4px" }} horizonAlign="center" gap={16}>
				<Button.Icon
					id="video"
					name={myMedia.video === "on" ? "CamOn24" : "CamOff24"}
					size="large"
					filled="transparent"
					fill="neutral_00_32"
					onClick={() => {
						const media = phoneCall.get_user_media(callID, Configure.userID);
						phoneCall.set_user_media(callID, Configure.userID, {
							...media,
							video: myMedia.video === "on" ? "off" : "on"
						});
					}}
				/>
				<Button.Icon
					id="audio"
					name={myMedia.audio === "on" ? "MicOn24" : "MicOff24"}
					size="large"
					filled="transparent"
					fill="neutral_00_32"
					onClick={() => {
						const media = phoneCall.get_user_media(callID, Configure.userID);
						phoneCall.set_user_media(callID, Configure.userID, {
							...media,
							audio: myMedia.audio === "on" ? "off" : "on"
						});
					}}
				/>
			</Row>
			<Row gap={8}>
				{callStatus !== "END" ? (
					<>
						<Button.Icon
							name="Call0124"
							filled="success"
							size="large"
							fill="neutral_00_100"
							style={{ width: "112px", height: "40px" }}
							disabled={isAccepted}
							onClick={handleAccept}
						/>
						<Button.Icon
							name="CallEnd24"
							filled="error"
							size="large"
							fill="neutral_00_100"
							style={{ width: "112px", height: "40px" }}
							onClick={handleReject}
						/>
					</>
				) : (
					<Typography typoSize="Title1_B" typoColor="neutral_00_100">
						{_T("종료됨")}
					</Typography>
				)}
			</Row>
			<Divider type="line" css={{ color: colors.neutral_50_20 }} />
			<Row
				verticalAlign="center"
				onClick={() => setIsOpen((prev) => !prev)}
				gap={4}
				style={{ cursor: "pointer" }}
			>
				<Icon name={isOpen ? "Up16" : "Down16"} size={16} fill={colors.neutral_00_100} />
				<Typography typoColor="neutral_00_100" typoSize="Body5">
					{_T("부재중 메시지 보내기")}
				</Typography>
			</Row>
			<Maybe test={isOpen}>
				<S.SelectWrapper gap={4} css={{ width: "100%" }}>
					<Menu.List style={{ width: "100%" }}>
						<Menu.Item onClick={handleRejectItem}>
							<Typography typoSize="Body3_MD">
								{_T("죄송합니다. 지금은 전화를 받을 수 없습니다. 나중에 연락주세요.")}
							</Typography>
						</Menu.Item>
						<Menu.Item onClick={handleRejectItem}>
							<Typography typoSize="Body3_MD">{_T("회의중이니 나중에 전화드리겠습니다.")}</Typography>
						</Menu.Item>
						<Menu.Item onClick={handleRejectItem}>
							<Typography typoSize="Body3_MD">{_T("곧 연락드리겠습니다.")}</Typography>
						</Menu.Item>
					</Menu.List>
					<Row gap={8} css={{ width: "100%" }}>
						<TextField
							placeholder={_T("메시지를 입력해주세요")}
							maxLength={50}
							onChange={(ev) => setCustomMessage(ev.target.value)}
						/>
						<Button
							size="Xlarge"
							style={{ height: "36px", width: "88px" }}
							onClick={() => sendRejectMessage(customMessage)}
							disabled={customMessage?.length === 0}
						>
							{_T("전송")}
						</Button>
					</Row>
				</S.SelectWrapper>
			</Maybe>
		</S.ReceivePopupWrapper>
	);
}
